import { useEffect, useMemo, useRef, useState } from 'react';
import { useStyles } from './style';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { GET_MY_STYLES } from 'graph_ql/styles/queries';
import { SEARCH_ITEM_CREATED } from 'graph_ql/search/search_item_created';
import config from 'config';
import AutoCompleteSearch from 'component/header-filter/auto_complete_input';
import { ITEM_SUGGESTION } from 'graph_ql/search/items_suggestion';
import { cache } from 'graph_ql';
import { STYLE_SUGGESTION } from 'graph_ql/search/style_suggestion';
import { sessionStyleFilterType, StylingSessionOutcome, StylingSessionOutcomeType } from '../type';
import { CREATE_STYLING_SESSION_OUTCOME } from 'graph_ql/styling_session_outcomes/mutations';
import { useParams } from 'react-router-dom';
import { Chip, CircularProgress } from '@material-ui/core';
import { GET_STYLING_SESSION_OUTCOMES } from 'graph_ql/styling_session_outcomes/queries';
import StandardPopup from 'component/StandardPopup';
import InfiniteScroll from 'react-infinite-scroll-component';
import { StandardAlert } from 'component/StandardAlert';

interface IAddStyleOrItemForSessionProps {
    outcomeType: StylingSessionOutcomeType;
    handleClose: Function;
    consumerId: string;
    consumerFullName: string;
    visible: boolean;
}

const AddStyleOrItemForSession = ({
    outcomeType,
    handleClose,
    consumerId,
    consumerFullName,
    visible,
}: IAddStyleOrItemForSessionProps) => {
    const classes = useStyles();
    const userData = useSelector((state: RootState) => state.userDetail.me);
    const [myList, setMyList] = useState<any[]>([]);
    const [searchInput, setSearchInput] = useState<string>('');
    const [suggestions, setSuggestions] = useState<Array<string>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { taffiId } = useParams<{ taffiId: string }>();
    const [stylingSessionOutcomes, setStylingSessionOutcomes] = useState<StylingSessionOutcome[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const getMoreTimeout = useRef<any>(null);
    const [activeIndex, setActiveIndex] = useState<sessionStyleFilterType>(sessionStyleFilterType.CONSUMER_STYLES);
    const [modalShow, setModalShow] = useState({
        show: false,
        title: '',
        content: '',
        button1: '',
        button2: '',
        path: '',
        type: '',
        buttonCancel: '',
        closeButton: true,
    });

    const [createStylingSessionOutcome, { loading: createStylingSessionOutcomeLoading }] = useMutation(
        CREATE_STYLING_SESSION_OUTCOME,
        {
            onCompleted(response) {
                handleClose();
            },
        },
    );

    const reset = () => {
        setActiveIndex(sessionStyleFilterType.CONSUMER_STYLES);
    };

    const styleSearchVariables = useMemo(() => {
        cache.modify({
            fields: {
                locationsInRadius(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        let index = config.algolia.algolia_prefix + 'styles';
        let filters = '';
        if (activeIndex === sessionStyleFilterType.CONSUMER_STYLES) {
            filters = `user.id: ${userData?.id} AND _consumersIdAndStatus: ${consumerId} AND (status: private OR status: published OR status: unlisted)`;
        }
        if (activeIndex === sessionStyleFilterType.ALL_STYLES) {
            filters = `user.id: ${userData?.id} AND (status: private OR status: published OR status: unlisted)`;
        }
        let query = '';
        if (searchInput) query += searchInput;
        return {
            index,
            query,
            filters,
            hit: config.algoliaHitPerPage,
            page: 0,
            facets: ['_consumersIdAndStatus'],
        };
        // eslint-disable-next-line
    }, [searchInput, userData, activeIndex]);

    const [getMyStyles, { refetch: refetchStyles, fetchMore: fetchMoreStyles }] = useLazyQuery(GET_MY_STYLES, {
        variables: styleSearchVariables,
        fetchPolicy: 'network-only',
        onCompleted(response) {
            onGetMyStyles(response, false);
        },
        onError(error) {
            console.error('get my styles data Failed: ', error);
        },
    });

    const onGetMyStyles = (response, fetch) => {
        setLoading(false);
        if (response?.locationsInRadius?.hits.length) {
            if (fetch) setMyList((prevList) => [...prevList, ...response?.locationsInRadius?.hits]);
            else setMyList(response?.locationsInRadius?.hits);
            if (response.locationsInRadius.page >= response.locationsInRadius.nbPages - 1) setHasMore(false);
        } else {
            setMyList([]);
            setHasMore(false);
        }
    };

    const itemSearchVariables = useMemo(() => {
        cache.modify({
            fields: {
                items(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        let index = config.algolia.algolia_prefix + 'item_created';
        let filters = `status: available AND creator:"${userData?.id}"`;
        let query = '';
        if (searchInput) query += searchInput;
        return {
            index,
            query: query,
            filters,
            hit: config.algoliaHitPerPage,
            page: 0,
            facets: [
                'title',
                'tags.name',
                'sizes.title',
                'accessLevel',
                'user.userName',
                'user.firstName',
                'user.LastName',
            ],
        };
    }, [userData, searchInput]);

    const [getMyItems, { refetch: refetchItems, fetchMore: fetchMoreItems }] = useLazyQuery(SEARCH_ITEM_CREATED, {
        fetchPolicy: 'network-only',
        variables: itemSearchVariables,
        onCompleted(response) {
            onGetMyItems(response, false);
        },
        onError(error) {
            console.error('get my items data Failed: ', error);
        },
    });

    const onGetMyItems = (response, fetch) => {
        setLoading(false);
        if (response?.items?.hits.length) {
            if (fetch) setMyList((prevList) => [...prevList, ...response?.items?.hits]);
            else setMyList(response?.items?.hits);
            if (response.items.page >= response.items.nbPages - 1) setHasMore(false);
        } else {
            setMyList([]);
            setHasMore(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.type === 'keydown') {
            setLoading(true);
            if (outcomeType === StylingSessionOutcomeType.Style) {
                setHasMore(true);
                setCurrentPage(0);
                refetchStyles({
                    ...styleSearchVariables,
                    query: e.suggestion ? e.suggestion : searchInput,
                }).then((response) => {
                    setLoading(false);
                    const { hits } = response?.data?.locationsInRadius;
                    setMyList(hits);
                });
            } else {
                refetchItems({
                    ...itemSearchVariables,
                    query: e.suggestion ? e.suggestion : searchInput,
                }).then((response) => {
                    setLoading(false);
                    const { hits } = response.data.items;
                    setMyList(hits);
                });
            }
        }
    };

    useEffect(() => {
        if (!visible) {
            reset();
        } else {
            refetchOutComes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    useEffect(() => {
        if (visible) {
            setCurrentPage(0);
            setHasMore(true);
            if (outcomeType === StylingSessionOutcomeType.Style) {
                setLoading(true);
                getMyStyles();
            }
            if (outcomeType === StylingSessionOutcomeType.Item) {
                setLoading(true);
                getMyItems();
            }
        }
    }, [outcomeType, getMyStyles, getMyItems, visible]);

    const [getItemSuggestions] = useLazyQuery(ITEM_SUGGESTION, {
        onCompleted(response) {
            const tempHits = [...response.locationsInRadius.hits];
            const tempSuggestions = tempHits.map((item) => item.query);
            setSuggestions(tempSuggestions);
        },
        onError(error) {
            console.error('get data Failed: ', error);
        },
    });

    const [getStyleSuggestions] = useLazyQuery(STYLE_SUGGESTION, {
        onCompleted(response) {
            const tempHits = [...response.locationsInRadius.hits];
            const tempSuggestions = tempHits.map((item) => item.query);
            setSuggestions(tempSuggestions);
        },
        onError(error) {
            console.error('get data Failed: ', error);
        },
    });

    const handleGetSuggestions = (input) => {
        if (outcomeType === StylingSessionOutcomeType.Style) {
            getStyleSuggestions({
                variables: {
                    index: config.algolia.algolia_prefix + 'styles_query_suggestions',
                    query: input,
                    filters: ``,
                    facets: [],
                },
            });
        } else {
            getItemSuggestions({
                variables: {
                    index: config.algolia.algolia_prefix + 'items_query_suggestions',
                    query: input,
                    filters: ``,
                    facets: [],
                },
            });
        }
    };

    useEffect(() => {
        handleGetSuggestions(searchInput);
        //eslint-disable-next-line
    }, [searchInput]);

    const { refetch: refetchOutComes, loading: fecthingOutcome } = useQuery(GET_STYLING_SESSION_OUTCOMES, {
        variables: {
            input: {
                sessionTaffiId: taffiId,
            },
        },
        onCompleted(response) {
            setStylingSessionOutcomes(response?.getStylingSessionOutcomes);
        },
        onError(error) {
            console.error('get styling session outcomes Failed: ', error);
        },
    });

    const renderStyleItems = (list: any[], selected: StylingSessionOutcome[]) => {
        const newArr: any[] = [];
        for (let item of list) {
            const index = selected.findIndex(
                (outcome) => outcome?.style?.id === item?.id || outcome?.item?.id === item?.id,
            );

            if (index >= 0) {
                newArr.push({ ...item, alreadyAdded: true });
            } else {
                newArr.push({ ...item, alreadyAdded: false });
            }
        }
        return newArr;
    };

    const handleSelectStyleItem = (product) => {
        createStylingSessionOutcome({
            variables: {
                input: {
                    consumerId: consumerId,
                    sessionTaffiId: taffiId,
                    type:
                        outcomeType === StylingSessionOutcomeType.Style
                            ? StylingSessionOutcomeType.Style
                            : StylingSessionOutcomeType.Item,
                    styleId: outcomeType === StylingSessionOutcomeType.Style ? product?.id : null,
                    itemId: outcomeType === StylingSessionOutcomeType.Item ? product?.id : null,
                    productTaffiId: product?.taffiId,
                },
            },
        });
    };

    const fetchMoreMyStyles = async (page: number) => {
        const data = await fetchMoreStyles({
            variables: {
                page: page,
            },
        });
        onGetMyStyles(data?.data, true);
    };

    const fetchMoreMyItems = async (page: number) => {
        const data = await fetchMoreItems({
            variables: {
                page: page,
            },
        });
        onGetMyItems(data?.data, true);
    };

    return (
        <div>
            <div className={classes.searchBox}>
                <AutoCompleteSearch
                    setInputSearch={setSearchInput}
                    handler={handleKeyDown}
                    value={searchInput}
                    suggestions={suggestions}
                    hasFilter={false}
                />
            </div>
            {outcomeType === StylingSessionOutcomeType.Style && (
                <div className={classes.filter}>
                    <div className={classes.filterSwiperWrapper}>
                        <Chip
                            classes={{
                                outlined: classes.chipOutline,
                                label:
                                    activeIndex === sessionStyleFilterType.CONSUMER_STYLES
                                        ? classes.chipLabelActive
                                        : classes.chipLabel,
                            }}
                            className={activeIndex === sessionStyleFilterType.CONSUMER_STYLES ? classes.chipActive : ''}
                            label={consumerFullName || 'Customer Styles'}
                            variant="outlined"
                            onClick={() => {
                                setHasMore(true);
                                setCurrentPage(0);
                                setActiveIndex(sessionStyleFilterType.CONSUMER_STYLES);
                            }}
                        />
                        <Chip
                            classes={{
                                outlined: classes.chipOutline,
                                label:
                                    activeIndex === sessionStyleFilterType.ALL_STYLES
                                        ? classes.chipLabelActive
                                        : classes.chipLabel,
                            }}
                            className={activeIndex === sessionStyleFilterType.ALL_STYLES ? classes.chipActive : ''}
                            label={'All Styles'}
                            variant="outlined"
                            onClick={() => {
                                setHasMore(true);
                                setCurrentPage(0);
                                setActiveIndex(sessionStyleFilterType.ALL_STYLES);
                            }}
                        />
                    </div>
                </div>
            )}
            {loading || fecthingOutcome ? (
                <div
                    style={{
                        height: '80vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <CircularProgress style={{ width: 20, height: 20, color: 'black' }} />
                </div>
            ) : (
                <div
                    className={classes.styleListItems}
                    id="scrollableDiv"
                    style={
                        outcomeType === StylingSessionOutcomeType.Item
                            ? { paddingTop: 30, height: '90vh', paddingBottom: 100 }
                            : {
                                paddingTop: !renderStyleItems(myList, stylingSessionOutcomes).length ? 0 : 15,
                                height: !renderStyleItems(myList, stylingSessionOutcomes).length ? '100%' : '90vh',
                                paddingBottom: !renderStyleItems(myList, stylingSessionOutcomes).length ? 0 : 100,
                            }
                    }
                >
                    <InfiniteScroll
                        dataLength={renderStyleItems(myList, stylingSessionOutcomes).length}
                        next={() => {
                            setCurrentPage((prev) => prev + 1);
                            if (getMoreTimeout.current) {
                                clearTimeout(getMoreTimeout.current);
                            }
                            getMoreTimeout.current = setTimeout(() => {
                                if (outcomeType === StylingSessionOutcomeType.Style) {
                                    fetchMoreMyStyles(currentPage + 1);
                                } else {
                                    fetchMoreMyItems(currentPage + 1);
                                }
                            }, 1500);
                        }}
                        hasMore={hasMore}
                        loader={
                            <div
                                style={
                                    !myList.length
                                        ? {
                                            height: '60vh',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }
                                        : { color: '#000000', width: '100%', textAlign: 'center' }
                                }
                            >
                                <CircularProgress style={{ width: 20, height: 20, marginTop: 10, color: 'black' }} />
                            </div>
                        }
                        scrollableTarget="scrollableDiv"
                    >
                        {Boolean(renderStyleItems(myList, stylingSessionOutcomes).length) &&
                            renderStyleItems(myList, stylingSessionOutcomes)?.map((listItem) => {
                                return (
                                    <div
                                        className={classes.styleListItem}
                                        key={listItem?.id}
                                        style={{ opacity: createStylingSessionOutcomeLoading ? 0.6 : 1 }}
                                    >
                                        <div className={classes.styleListItemLeft}>
                                            <img src={listItem?.images[0]} alt="" />
                                        </div>
                                        <div className={classes.styleListItemRight}>
                                            <div className={classes.styleListItemRightContent}>
                                                <h3>{listItem?.title}</h3>
                                                <div className={classes.styleListItemBadge}>{listItem?.status}</div>
                                            </div>
                                            {listItem.alreadyAdded !== true && (
                                                <button
                                                    onClick={() => {
                                                        handleSelectStyleItem(listItem);
                                                    }}
                                                >
                                                    Select
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                    </InfiniteScroll>
                </div>
            )}
            {!renderStyleItems(myList, stylingSessionOutcomes).length && (
                <div className={classes.noStyleItems}>
                    <StandardAlert
                        height={'80vh'}
                        title={
                            activeIndex === sessionStyleFilterType.CONSUMER_STYLES
                                ? searchInput.length
                                    ? 'No Matching Styles'
                                    : `You haven't created any Private Styles for ${
                                        consumerFullName || 'this consumer'
                                    }.`
                                : activeIndex === sessionStyleFilterType.ALL_STYLES
                                    ? 'No Matching Styles.'
                                    : 'No Matching Items.'
                        }
                        isIcon={false}
                    />
                </div>
            )}
            {!renderStyleItems(myList, stylingSessionOutcomes).length && (
                <div className={classes.noStyleItems}>
                    <StandardAlert
                        height={'80vh'}
                        title={
                            activeIndex === sessionStyleFilterType.CONSUMER_STYLES
                                ? searchInput.length
                                    ? 'No Matching Styles'
                                    : `You haven't created any Private Styles for ${
                                        consumerFullName || 'this consumer'
                                    }.`
                                : activeIndex === sessionStyleFilterType.ALL_STYLES
                                    ? 'No Matching Styles.'
                                    : 'No Matching Items.'
                        }
                        isIcon={false}
                    />
                </div>
            )}
            {!renderStyleItems(myList, stylingSessionOutcomes).length && (
                <div className={classes.noStyleItems}>
                    <StandardAlert
                        height={'80vh'}
                        title={
                            activeIndex === sessionStyleFilterType.CONSUMER_STYLES
                                ? searchInput.length
                                    ? 'No Matching Styles'
                                    : `You haven't created any Private Styles for ${
                                        consumerFullName || 'this consumer'
                                    }.`
                                : activeIndex === sessionStyleFilterType.ALL_STYLES
                                    ? 'No Matching Styles.'
                                    : 'No Matching Items.'
                        }
                        isIcon={false}
                    />
                </div>
            )}
            <StandardPopup
                show={modalShow.show}
                onHide={() => setModalShow({ ...modalShow, show: false })}
                title={modalShow.title}
                content={modalShow.content}
                button1={modalShow.button1}
                buttonCancel={modalShow.buttonCancel}
                type={modalShow.type as any}
                closeButton={modalShow.closeButton}
            />
            {createStylingSessionOutcomeLoading && (
                <div className={classes.overLayLoadingContainer}>
                    <CircularProgress style={{ width: 30, height: 30, color: 'black' }} />
                </div>
            )}
        </div>
    );
};

export default AddStyleOrItemForSession;
